import React, { useEffect, useState } from "react";
import { Menu, X, Phone, ShieldCheck, Tag } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { nav, company } from "../mock";
import { Button } from "./ui/button";
import Logo from "./Logo";

// Renders a nav link: react-router Link for internal routes ("/..."),
// and a plain anchor for hash links (prefixed with "/" when off home so it scrolls after navigating home).
function NavLink({ item, onHome, className, onClick }) {
  if (item.href.startsWith("/")) {
    return <Link to={item.href} className={className} onClick={onClick}>{item.label}</Link>;
  }
  const href = onHome ? item.href : `/${item.href}`;
  return <a href={href} className={className} onClick={onClick}>{item.label}</a>;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const onHome = useLocation().pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // Runs once on mount to attach a single scroll listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Top utility bar */}
      <div className="hidden md:block hpr-gradient text-white text-[13px]">
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-10">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#38BDF8]" />
            <span className="font-medium">Egna certifierade takproffs</span>
            <span className="text-white/70">· 10 års garanti på takmålning</span>
          </div>
          <div className="flex items-center gap-6 shrink-0">
            <a href={onHome ? "#kontakt" : "/#kontakt"} data-testid="header-campaign-link" className="inline-flex items-center gap-1.5 font-bold text-[#38BDF8] hover:text-white transition-colors"><Tag className="w-3.5 h-3.5" /> 20% kampanjrabatt</a>
            <a href={`mailto:${company.email}`} className="text-white/90 hover:text-white transition-colors whitespace-nowrap">{company.email}</a>
            <a href={company.phoneHref} className="flex items-center gap-2 hover:text-white text-white/90 transition-colors whitespace-nowrap">
              <Phone className="w-4 h-4 shrink-0" /> {company.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#070e24]/90 backdrop-blur-md shadow-[0_6px_30px_-12px_rgba(56,189,248,0.35)]" : "bg-[#070e24]"}`}>
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-[72px]">
          <Link to="/" aria-label="HPR hem" className="shrink-0" data-testid="header-home-link"><Logo variant="light" /></Link>

          <nav className="hidden xl:flex items-center gap-0.5">
            {nav.map((n) => (
              <NavLink
                key={n.href}
                item={n}
                onHome={onHome}
                className="px-3 py-2 rounded-full text-[14px] font-medium text-slate-200 hover:text-[#38BDF8] hover:bg-white/5 transition-colors whitespace-nowrap"
              />
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3 shrink-0">
            <a href={company.phoneHref} className="flex items-center gap-2 text-[14px] font-semibold text-[#38BDF8] whitespace-nowrap" data-testid="header-phone-link">
              <Phone className="w-4 h-4 shrink-0" /> {company.phone}
            </a>
            <Button asChild className="rounded-full h-11 px-6 bg-[#1B45C4] hover:bg-[#2953d6] text-white font-semibold whitespace-nowrap">
              <a href={onHome ? "#kontakt" : "/#kontakt"} data-testid="header-quote-cta">Få offert</a>
            </Button>
          </div>

          <button className="xl:hidden -mr-2 p-3 text-[#38BDF8]" onClick={() => setOpen(true)} aria-label="Öppna meny" data-testid="mobile-menu-open">
            <Menu className="w-7 h-7" strokeWidth={2.5} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] xl:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} data-testid="mobile-menu-backdrop" />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-[#0a1330] shadow-2xl p-5 sm:p-6 flex flex-col animate-in slide-in-from-right" data-testid="mobile-menu-drawer">
            <div className="flex items-center justify-between mb-8">
              <Logo variant="light" />
              <button onClick={() => setOpen(false)} aria-label="Stäng meny" className="p-2 -mr-2" data-testid="mobile-menu-close"><X className="w-7 h-7 text-slate-200" /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {nav.map((n) => (
                <NavLink key={n.href} item={n} onHome={onHome} onClick={() => setOpen(false)}
                  className="px-4 py-3 rounded-xl text-lg font-medium text-slate-100 hover:bg-white/5 hover:text-[#38BDF8]" />
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-3">
              <a href={company.phoneHref} className="flex items-center justify-center gap-2 h-12 rounded-full border-2 border-[#38BDF8] text-[#38BDF8] font-semibold whitespace-nowrap" data-testid="mobile-menu-phone-link">
                <Phone className="w-4 h-4 shrink-0" /> {company.phone}
              </a>
              <Button asChild className="rounded-full h-12 bg-[#1B45C4] hover:bg-[#12308f] text-white font-semibold">
                <a href={onHome ? "#kontakt" : "/#kontakt"} onClick={() => setOpen(false)} data-testid="mobile-menu-quote-cta">Få offert</a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
